import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import { Icon } from "../components/common/Icon";
import ConfirmModal from "../components/common/ConfirmModal";
import SearchableSelect from "../components/common/SearchableSelect";
import {
  DEFAULT_LADDER_CONFIG,
  LadderConfigFields,
  type LadderConfigValues,
} from "../components/ladders/LadderConfigForm";
import { useLadderDetail } from "../hooks/queries/useLadder";
import { useUsers } from "../hooks/queries/useUsers";
import {
  useAddOrganizer,
  useRemoveOrganizer,
  useTransferLadderOwnership,
  useUpdateLadder,
} from "../hooks/mutations/useLadderMutations";
import { getLoggedUser, isAdmin } from "../util/SecurityUtils";
import { playerToString } from "../util/StringUtils";
import { useIsMobile } from "../hooks/useIsMobile";
import type { LadderDetail as LadderDetailType } from "../types/domain";
import type { UserSummary } from "../api/users";

/** Display name of the player linked to a user, or "" if the user has no linked player. */
function playerNameForUser(u: UserSummary): string {
  return u.playerId
    ? playerToString({
        firstname: u.playerFirstname ?? "",
        lastname: u.playerLastname ?? "",
        nickname: u.playerNickname ?? null,
      })
    : "";
}

/**
 * Build a display + search descriptor for a user picker option. Player name (if the user is
 * linked to a player) shows next to the username in the dropdown row and is included in the
 * search haystack so users can find people by either handle.
 */
function toUserOption(u: UserSummary) {
  const playerName = playerNameForUser(u);
  return {
    value: u.id,
    label: u.username,
    secondary: playerName || null,
    searchTokens: [playerName, u.playerFirstname ?? "", u.playerLastname ?? "", u.playerNickname ?? ""],
  };
}

function LadderSettings() {
  const { id } = useParams();
  const ladderId = Number(id);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const isMobile = useIsMobile();
  const { data: ladder, isLoading } = useLadderDetail(ladderId);
  const { data: users = [] } = useUsers();
  const updateLadder = useUpdateLadder();
  const addOrganizer = useAddOrganizer();
  const removeOrganizer = useRemoveOrganizer();
  const transferOwner = useTransferLadderOwnership();

  const [config, setConfig] = useState<LadderConfigValues>(DEFAULT_LADDER_CONFIG);
  const [newOrgUserId, setNewOrgUserId] = useState<string>("");
  const [dirty, setDirty] = useState(false);
  const [organizerToRemove, setOrganizerToRemove] = useState<{ userId: number; username: string } | null>(null);

  // Hydrate the form once the ladder finishes loading. We deliberately don't reset while the user
  // has unsaved edits, so re-fetches don't blow away in-progress work.
  useEffect(() => {
    if (!ladder || dirty) return;
    setConfig({
      nameCs: ladder.name,
      nameEn: ladder.nameEn,
      calculateElo: ladder.calculateElo,
      discountCloseLoss: ladder.discountCloseLoss,
      calculateRating: ladder.calculateRating,
      awardAchievements: ladder.awardAchievements,
    });
  }, [ladder, dirty]);

  if (isLoading || !ladder) return null;

  const loggedUser = getLoggedUser();
  const isOwner = loggedUser?.id != null && loggedUser.id === ladder.ownerUserId;
  const canManage = isAdmin() || isOwner;

  if (!canManage) {
    return (
      <div className="base-container">
        <div className="table-header header">
          <span className="table-title">{t("ladderSettings")}</span>
        </div>
        <div className="content">
          <p>{t("notAuthorized") ?? "You are not allowed to change these settings."}</p>
        </div>
      </div>
    );
  }

  const playerNameByUserId = new Map(users.map((u) => [u.id, playerNameForUser(u)]));
  const playerNameFor = (userId: number | null | undefined): string =>
    (userId != null ? playerNameByUserId.get(userId) : "") ?? "";

  const displayName = i18n.language === "cs" ? ladder.name : ladder.nameEn;
  // Names/config on official ladders are managed centrally — settings for those focus on
  // organizers only. Custom (unofficial) ladders expose the full editing surface.
  const isReadOnlyLadderMeta = ladder.isOfficial;

  const saveMeta = async () => {
    await updateLadder.mutateAsync({
      id: ladderId,
      payload: { ...config },
    });
    setDirty(false);
  };

  return (
    <div className="base-container">
      <div className="table-header header">
        <span className="table-title ladder-settings-title">
          {isMobile ? displayName : `${t("ladderSettings")}: ${displayName}`}
        </span>
        <Button
          variant="outline-light"
          size="sm"
          onClick={() => navigate(-1)}
          className="table-button table-button-icon"
          aria-label={t("close") ?? "Close"}
          title={t("close") ?? "Close"}
        >
          <Icon name="X" />
        </Button>
      </div>
      <div className="content">
        <div className="new-ladder-wrapper">
          {!isReadOnlyLadderMeta ? (
            <Card className="new-ladder-card mb-3">
              <Card.Body>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveMeta();
                  }}
                >
                  <LadderConfigFields
                    value={config}
                    onChange={(patch) => {
                      setConfig((c) => ({ ...c, ...patch }));
                      setDirty(true);
                    }}
                    idPrefix="ladderSettings"
                  />

                  <div className="new-ladder-actions">
                    <Button type="submit" variant="success" disabled={!dirty || updateLadder.isPending}>
                      {t("save") ?? "Save"}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          ) : null}

          <Card className="new-ladder-card mb-3">
            <Card.Body>
              <h6 className="new-ladder-section-title">{t("ladderOrganizers")}</h6>
              <div className="mb-2">
                <strong>{t("ladderOwner")}:</strong> {ladder.ownerUsername ?? "-"}
                {playerNameFor(ladder.ownerUserId) ? (
                  <span className="ladder-organizer-playername"> ({playerNameFor(ladder.ownerUserId)})</span>
                ) : null}
              </div>
              <ul className="list-unstyled mb-3">
                {ladder.organizers.map((org) => (
                  <li key={org.userId} className="ladder-organizer-row">
                    <span>
                      {org.username}
                      {playerNameFor(org.userId) ? (
                        <span className="ladder-organizer-playername"> ({playerNameFor(org.userId)})</span>
                      ) : null}
                      {org.isOwner ? ` (${t("ladderOwner")})` : ""}
                    </span>
                    {!org.isOwner ? (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={removeOrganizer.isPending}
                        onClick={() => setOrganizerToRemove({ userId: org.userId, username: org.username })}
                      >
                        <Icon name="X" />
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>

              <AddOrganizerForm
                ladder={ladder}
                users={users}
                newOrgUserId={newOrgUserId}
                setNewOrgUserId={setNewOrgUserId}
                onAdd={async (userId) => {
                  await addOrganizer.mutateAsync({ ladderId, userId });
                  setNewOrgUserId("");
                }}
                busy={addOrganizer.isPending}
              />

              <TransferOwnerForm
                ladder={ladder}
                users={users}
                onTransfer={(newOwnerUserId) =>
                  transferOwner.mutate({ ladderId, newOwnerUserId })
                }
              />
            </Card.Body>
          </Card>
        </div>
      </div>
      {organizerToRemove ? (
        <ConfirmModal
          title={t("removeOrganizer")}
          message={t("removeOrganizerConfirmation", { username: organizerToRemove.username })}
          onCancel={() => setOrganizerToRemove(null)}
          onConfirm={() => {
            removeOrganizer.mutate({ ladderId, userId: organizerToRemove.userId });
            setOrganizerToRemove(null);
          }}
        />
      ) : null}
    </div>
  );
}

interface AddOrganizerFormProps {
  ladder: LadderDetailType;
  users: UserSummary[];
  newOrgUserId: string;
  setNewOrgUserId: (v: string) => void;
  onAdd: (userId: number) => void;
  busy: boolean;
}

function AddOrganizerForm({
  ladder,
  users,
  newOrgUserId,
  setNewOrgUserId,
  onAdd,
  busy,
}: AddOrganizerFormProps) {
  const { t } = useTranslation();
  const availableUsers = users.filter(
    (u) => !ladder.organizers.some((o) => o.userId === u.id),
  );
  if (availableUsers.length === 0) return null;
  return (
    <Form.Group className="ladder-inline-form mb-3">
      <Form.Label>{t("addOrganizer")}</Form.Label>
      <div className="ladder-inline-form-row">
        <SearchableSelect
          className="ladder-inline-form-select"
          options={availableUsers.map(toUserOption)}
          value={newOrgUserId}
          onChange={(v) => setNewOrgUserId(v === "" ? "" : String(v))}
          placeholder={t("search") ?? "Search"}
        />
        <Button
          variant="success"
          disabled={!newOrgUserId || busy}
          onClick={() => onAdd(Number(newOrgUserId))}
          className="ladder-inline-form-button"
        >
          {t("addOrganizer")}
        </Button>
      </div>
    </Form.Group>
  );
}

interface TransferOwnerFormProps {
  ladder: LadderDetailType;
  users: UserSummary[];
  onTransfer: (newOwnerUserId: number) => void;
}

function TransferOwnerForm({ ladder, users, onTransfer }: TransferOwnerFormProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>("");
  const [confirming, setConfirming] = useState(false);
  const candidates = ladder.organizers.filter((o) => !o.isOwner);
  const usersById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  if (candidates.length === 0) return null;
  const selectedUsername = candidates.find((o) => o.userId === Number(selected))?.username ?? "";
  return (
    <Form.Group className="ladder-inline-form">
      <Form.Label>{t("transferOwnership")}</Form.Label>
      <div className="ladder-inline-form-row">
        <SearchableSelect
          className="ladder-inline-form-select"
          options={candidates.map((o) => {
            const fullUser = usersById.get(o.userId);
            return fullUser
              ? toUserOption(fullUser)
              : { value: o.userId, label: o.username };
          })}
          value={selected}
          onChange={(v) => setSelected(v === "" ? "" : String(v))}
          placeholder={t("search") ?? "Search"}
        />
        <Button
          variant="warning"
          disabled={!selected}
          onClick={() => setConfirming(true)}
          className="ladder-inline-form-button"
        >
          {t("transferOwnership")}
        </Button>
      </div>
      {confirming ? (
        <ConfirmModal
          title={t("transferOwnership")}
          message={t("transferOwnershipConfirmation", { username: selectedUsername })}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            onTransfer(Number(selected));
            setSelected("");
            setConfirming(false);
          }}
        />
      ) : null}
    </Form.Group>
  );
}

export default LadderSettings;
